import { Link } from "react-router-dom";
import { ArrowRight, Globe2, LineChart, Layers, Camera, TrendingUp, TrendingDown, ShieldCheck, Trophy, Flame, Calendar, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { setCardListContext } from "@/lib/cardContext";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { useCurrency } from "@/context/CurrencyContext";

export default function Home() {
  const { t, i18n } = useTranslation();
  const { format } = useCurrency();
  const [featuredSets, setFeaturedSets] = useState([]);
  const [trendingUp, setTrendingUp] = useState([]);
  const [trendingDown, setTrendingDown] = useState([]);
  const [globalTop, setGlobalTop] = useState([]);
  const [topPerSet, setTopPerSet] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    api.get("/sets", { params: { pageSize: 8, orderBy: "-releaseDate" } })
      .then((r) => setFeaturedSets(r.data.data || []))
      .catch(() => setFeaturedSets([]));
    api.get("/trending", { params: { direction: "up", limit: 8 } })
      .then((r) => setTrendingUp(r.data.items || []))
      .catch(() => setTrendingUp([]));
    api.get("/trending", { params: { direction: "down", limit: 8 } })
      .then((r) => setTrendingDown(r.data.items || []))
      .catch(() => setTrendingDown([]));
    api.get("/top-cards", { params: { limit: 12 } })
      .then((r) => setGlobalTop(r.data.items || []))
      .catch(() => setGlobalTop([]));
    api.get("/top-cards", { params: { per_set: 4 } })
      .then((r) => setTopPerSet(r.data.groups || []))
      .catch(() => setTopPerSet([]));
    api.get("/new-releases", { params: { limit: 4 } })
      .then((r) => setNewReleases(r.data.items || []))
      .catch(() => setNewReleases([]));
    api.get("/upcoming-releases", { params: { limit: 3 } })
      .then((r) => setUpcoming(r.data.items || []))
      .catch(() => setUpcoming([]));
  }, [i18n.language]);

  return (
    <div className="page-enter bg-[#FAFAF7]">
      <Seo
        title="Global Pokémon TCG price index"
        description="Track every Pokémon card price in every language. Real sales from Cardmarket and TCGPlayer, organized by set."
        url={window.location.origin + "/"}
      />

      {/* HERO */}
      <section className="relative overflow-hidden warm-bg border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 pt-16 sm:pt-20 pb-20 sm:pb-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="lang-chip live"><span className="live-dot" /> {t("home.live_index", "Live index")}</span>
              <span className="lang-chip">EN · FR · DE · ES · IT · JP · KR · ZH</span>
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6 text-slate-900">
              {t("home.hero_title_1")}{" "}
              <span className="text-[#E11D48]">{t("home.hero_title_3")}</span>{" "}
              {t("home.hero_title_4")}
            </h1>
            <p className="text-slate-600 text-base sm:text-lg max-w-xl leading-relaxed mb-8">{t("home.hero_subtitle")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/sets" data-testid="hero-browse-sets-btn" className="btn-primary text-sm inline-flex items-center gap-2">
                {t("home.browse_sets")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/scan" data-testid="hero-scan-btn" className="btn-ghost text-sm inline-flex items-center gap-2">
                <Camera className="w-4 h-4 text-[#2563EB]" /> {t("home.scan_card", "Scan a card")}
              </Link>
              <Link to="/search" data-testid="hero-search-cards-btn" className="btn-ghost text-sm">
                {t("home.search_card")}
              </Link>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> {t("home.real_prices", "Real market prices")}</span>
              <span className="hidden sm:inline-flex items-center gap-1.5"><Globe2 className="w-4 h-4 text-[#2563EB]" /> 8 languages</span>
            </div>
          </motion.div>

          {/* Hero side card stack */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative hidden sm:flex justify-center items-center min-h-[320px]"
          >
            {featuredSets.slice(0, 3).map((s, i) => (
              <div
                key={s.id}
                className="absolute flat-card p-5 w-48 sm:w-56"
                style={{
                  transform: `rotate(${(i - 1) * 6}deg) translateX(${(i - 1) * 60}px)`,
                  zIndex: 3 - i,
                }}
              >
                <div className="h-16 flex items-center mb-3">
                  {s.images?.logo && <img src={s.images.logo} alt={s.name} className="max-h-12 max-w-full object-contain" />}
                </div>
                <div className="font-semibold text-sm text-slate-900 truncate">{s.name}</div>
                <div className="text-xs text-slate-500 mt-1">{s.releaseDate} · {s.total} cards</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Trending */}
      {(trendingUp.length > 0 || trendingDown.length > 0) && (
        <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-8">
          <TrendingList
            title={t("home.trending_up", "Top risers this week")}
            items={trendingUp}
            direction="up"
            icon={TrendingUp}
            testid="trending-up"
          />
          <TrendingList
            title={t("home.trending_down", "Biggest drops this week")}
            items={trendingDown}
            direction="down"
            icon={TrendingDown}
            testid="trending-down"
          />
        </section>
      )}

      {/* Upcoming Releases Countdown */}
      {upcoming.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-12">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-[#2563EB]" />
                <span className="text-xs font-bold uppercase tracking-widest text-[#2563EB]">{t("home.upcoming_eyebrow", "À venir")}</span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-slate-900">
                {t("home.upcoming_title", "Prochaines sorties")}
              </h2>
              <p className="text-slate-500 mt-1 text-sm">
                {t("home.upcoming_desc", "Les expansions Pokémon TCG officiellement annoncées — préparez votre wallet.")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcoming.map((s, i) => (
              <motion.div
                key={s.id + "-" + s.language}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className="flat-card overflow-hidden relative"
                data-testid={`upcoming-${s.id}`}
              >
                <div className="absolute top-3 right-3 z-10">
                  <span className="bg-[#2563EB] text-white text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full">
                    {s.language === "ja" ? "JP" : (s.language === "fr" ? "FR" : "EN")}
                  </span>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-5 pb-3 min-h-[120px] flex items-center justify-center">
                  {s.logo ? (
                    <img src={s.logo} alt={s.name} className="max-h-20 max-w-full object-contain drop-shadow-sm" />
                  ) : (
                    <Sparkles className="w-12 h-12 text-blue-300" />
                  )}
                </div>
                <div className="p-5">
                  <div className="font-heading font-bold text-lg text-slate-900 mb-1">{s.name}</div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-4 font-mono">
                    <Calendar className="w-3 h-3" /> {s.releaseDate}
                    {s.cards_count && <span>· {s.cards_count} {t("set_detail.cards", "cartes")}</span>}
                  </div>

                  <div className="flex items-center justify-center p-3 rounded-lg bg-gradient-to-br from-[#E11D48]/10 to-[#2563EB]/10 border border-slate-100">
                    <div className="text-center">
                      <div className="font-heading font-extrabold text-4xl text-[#E11D48] tabular-nums">
                        {s.days_until}
                      </div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mt-1">
                        {s.days_until === 1 ? t("home.day_until", "jour avant sortie") : t("home.days_until", "jours avant sortie")}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* New Releases Banner — Recent sets with top card preview & values */}
      {newReleases.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-12">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-emerald-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">{t("home.new_releases_eyebrow", "Nouveautés")}</span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-slate-900">
                {t("home.new_releases_title", "Sorties récentes")}
              </h2>
              <p className="text-slate-500 mt-1 text-sm">
                {t("home.new_releases_desc", "Les dernières expansions Pokémon TCG avec leur valeur totale et la carte chase à surveiller.")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {newReleases.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                <Link
                  to={`/sets/${s.id}`}
                  data-testid={`new-release-${s.id}`}
                  className="flat-card overflow-hidden block group hover:-translate-y-1 hover:shadow-xl transition-all"
                >
                  <div className="relative bg-gradient-to-br from-emerald-50 to-amber-50 p-5 pb-3 min-h-[140px] flex items-center justify-center">
                    {(s.days_since_release !== null && s.days_since_release <= 30) && (
                      <span className="absolute top-3 left-3 bg-emerald-500 text-white text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full">
                        {s.days_since_release === 0 ? t("home.released_today", "Today") : t("home.days_ago", "{{n}}j", { n: s.days_since_release })}
                      </span>
                    )}
                    {s.logo && <img src={s.logo} alt={s.name} className="max-h-20 max-w-full object-contain drop-shadow-sm" />}
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-bold text-base text-slate-900 truncate">{s.name}</div>
                        <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-mono mt-0.5">
                          <Calendar className="w-3 h-3" /> {s.releaseDate} · {s.cards_count} {t("set_detail.cards", "cartes")}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#E11D48] group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                    </div>

                    {/* Set total value */}
                    <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50 mb-3">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                        {t("home.set_value", "Valeur du set")}
                      </span>
                      <span className="font-heading font-extrabold text-base text-slate-900">{format(s.total_value_eur, "EUR")}</span>
                    </div>

                    {/* Top card preview */}
                    {s.top_card && (
                      <div className="flex items-center gap-2.5 p-2 rounded-lg border border-amber-100 bg-amber-50/40">
                        {s.top_card.image && (
                          <img src={s.top_card.image} alt={s.top_card.name} loading="lazy" className="w-9 h-12 object-cover rounded shadow-sm flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-amber-600 font-bold flex items-center gap-1">
                            <Trophy className="w-3 h-3" /> {t("home.chase_card", "Chase card")}
                          </div>
                          <div className="font-semibold text-xs text-slate-900 truncate">{s.top_card.name}</div>
                          <div className="font-bold text-sm text-[#E11D48]">{format(s.top_card.price_eur, "EUR")}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Featured sets */}
      {featuredSets.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8 pb-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-slate-900">{t("home.latest_sets", "Latest sets")}</h2>
              <p className="text-slate-500 mt-1">{t("home.latest_sets_desc", "Just-released expansions, ready to track.")}</p>
            </div>
            <Link to="/sets" className="text-sm font-semibold text-[#E11D48] hover:underline">{t("home.see_all", "See all")} →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {featuredSets.slice(0, 8).map((s) => (
              <Link
                key={s.id}
                to={`/sets/${s.id}`}
                data-testid={`home-set-${s.id}`}
                className="flat-card p-5 group shimmer"
              >
                <div className="h-20 flex items-center mb-3">
                  {s.images?.logo ? (
                    <img src={s.images.logo} alt={s.name} className="max-h-16 max-w-full object-contain" />
                  ) : <Layers className="w-10 h-10 text-slate-300" />}
                </div>
                <div className="font-semibold text-sm text-slate-900 truncate">{s.name}</div>
                <div className="text-xs text-slate-500 mt-1">{s.releaseDate} · {s.total} cards</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Global Top 12 — Most Expensive Cards */}
      {globalTop.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12 bg-gradient-to-b from-amber-50/40 to-transparent">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-amber-600">{t("home.hall_of_fame", "Hall of Fame")}</span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-slate-900">
                {t("home.top_global_title", "Top 12 cartes les plus chères")}
              </h2>
              <p className="text-slate-500 mt-1 text-sm">
                {t("home.top_global_desc", "Les cartes les plus précieuses, tous sets confondus.")}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {globalTop.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.3, delay: Math.min(i, 12) * 0.04 }}
              >
                <Link
                  to={`/cards/${c.id}`}
                  onClick={() => setCardListContext(
                    globalTop.map((cc) => cc.id),
                    { url: "/", label: t("home.top_global_title", "Hall of Fame") },
                  )}
                  data-testid={`top-global-${c.id}`}
                  className="flat-card overflow-hidden group block relative hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                  {i < 3 && (
                    <div className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                      i === 0 ? "bg-amber-400 text-amber-900" :
                      i === 1 ? "bg-slate-300 text-slate-700" :
                      "bg-amber-700 text-amber-50"
                    }`}>
                      #{i + 1}
                    </div>
                  )}
                  {i >= 3 && (
                    <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-slate-100 text-slate-500">
                      #{i + 1}
                    </div>
                  )}
                  <div className="aspect-[2.5/3.5] bg-slate-50 overflow-hidden">
                    {c.image ? (
                      <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">no image</div>}
                  </div>
                  <div className="p-2.5">
                    <div className="font-heading font-bold text-xs truncate text-slate-900">{c.name}</div>
                    <div className="text-[10px] text-slate-400 truncate">{c.set_name}</div>
                    <div className="font-bold text-sm text-amber-600 mt-1">{format(c.price_eur, "EUR")}</div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Per-Set Top 4 — Most Expensive Cards in Recent Sets */}
      {topPerSet.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-5 h-5 text-[#E11D48]" />
                <span className="text-xs font-bold uppercase tracking-widest text-[#E11D48]">{t("home.chase_cards", "Chase cards")}</span>
              </div>
              <h2 className="font-heading font-extrabold text-3xl tracking-tight text-slate-900">
                {t("home.top_per_set_title", "Top cartes par série récente")}
              </h2>
              <p className="text-slate-500 mt-1 text-sm">
                {t("home.top_per_set_desc", "Les 4 cartes les plus précieuses de chaque sortie récente.")}
              </p>
            </div>
            <Link to="/sets" className="text-sm font-semibold text-[#E11D48] hover:underline whitespace-nowrap">{t("home.see_all", "See all")} →</Link>
          </div>

          <div className="space-y-8">
            {topPerSet.slice(0, 6).map((g, gi) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.3, delay: gi * 0.05 }}
                data-testid={`top-set-${g.id}`}
              >
                <Link
                  to={`/sets/${g.id}`}
                  className="flex items-center gap-3 mb-3 group"
                >
                  {g.logo && <img src={g.logo} alt={g.name} className="h-10 max-w-[160px] object-contain" />}
                  <div>
                    <div className="font-heading font-bold text-lg text-slate-900 group-hover:text-[#E11D48] transition-colors">
                      {g.name}
                    </div>
                    <div className="text-xs text-slate-500">{g.releaseDate}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#E11D48] group-hover:translate-x-1 transition-all ml-2" />
                </Link>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {g.top_cards.slice(0, 4).map((c, i) => (
                    <Link
                      key={c.id}
                      to={`/cards/${c.id}`}
                      onClick={() => setCardListContext(
                        g.top_cards.slice(0, 4).map((cc) => cc.id),
                        { url: `/sets/${g.id}`, label: g.name },
                      )}
                      data-testid={`top-set-${g.id}-card-${c.id}`}
                      className="flat-card overflow-hidden group block hover:-translate-y-1 hover:shadow-lg transition-all"
                    >
                      <div className="aspect-[2.5/3.5] bg-slate-50 overflow-hidden">
                        {c.image ? (
                          <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        ) : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">no image</div>}
                      </div>
                      <div className="p-2.5">
                        <div className="font-heading font-bold text-xs truncate text-slate-900">{c.name}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-slate-400">#{c.number}</span>
                          <span className="font-bold text-sm text-[#E11D48]">{format(c.price_eur, "EUR")}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Feature grid */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-6">
        {[
          { icon: Layers, title: t("home.features.by_set_title"), text: t("home.features.by_set_desc"), color: "text-[#E11D48]", bg: "bg-red-50" },
          { icon: Globe2, title: t("home.features.langs_title"), text: t("home.features.langs_desc"), color: "text-[#2563EB]", bg: "bg-blue-50" },
          { icon: LineChart, title: t("home.features.real_title"), text: t("home.features.real_desc"), color: "text-emerald-600", bg: "bg-emerald-50" },
        ].map(({ icon: Icon, title, text, color, bg }, i) => (
          <motion.div
            key={title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="flat-card p-7"
          >
            <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center mb-5`}>
              <Icon className={`w-6 h-6 ${color}`} strokeWidth={2} />
            </div>
            <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">{title}</h3>
            <p className="text-slate-600 leading-relaxed text-sm">{text}</p>
          </motion.div>
        ))}
      </section>
    </div>
  );
}

function TrendingList({ title, items, direction, icon: Icon, testid }) {
  const colorClass = direction === "up" ? "text-emerald-600" : "text-[#E11D48]";
  const bgClass = direction === "up" ? "bg-emerald-50" : "bg-red-50";
  if (!items.length) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-9 h-9 ${bgClass} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${colorClass}`} />
        </div>
        <h2 className="font-heading font-bold text-xl text-slate-900">{title}</h2>
      </div>
      <div className="flat-card overflow-hidden">
        {items.slice(0, 6).map((m, i) => (
          <Link
            key={m.card.id}
            to={`/cards/${m.card.id}`}
            onClick={() => setCardListContext(
              items.slice(0, 6).map((mm) => mm.card.id),
              { url: "/", label: title },
            )}
            data-testid={`${testid}-item-${m.card.id}`}
            className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition"
          >
            <span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span>
            <div className="w-10 h-14 rounded-md overflow-hidden bg-slate-100 flex-shrink-0">
              {m.card.image && <img src={m.card.image} alt={m.card.name} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-900 truncate">{m.card.name}</div>
              <div className="text-xs text-slate-500 truncate">{m.card.set?.name} · #{m.card.number}</div>
            </div>
            <div className="text-right">
              <div className={`text-sm font-bold ${colorClass}`}>{direction === "up" ? "+" : ""}{m.delta_pct}%</div>
              <div className="text-xs text-slate-500">€{m.price_now}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
